/**
 * requestId 会话（幂等键）—— 方案 §5.1 / 对接文档 §6
 *
 * 后端靠 `uk_iter_request(iteration_id, request_id)` 拦连点【确定】，前端职责三条：
 *   1. 同一次弹窗会话共用一个 ID（连点被幂等吞掉，正是本意）；
 *   2. 换会话必须换新 ID；
 *   3. 🔴 任何时候都不许持久化（localStorage / sessionStorage / URL / 表单草稿都不行）。
 *
 * 复用旧 requestId 的后果（对接文档 §6 原文）：命中幂等 → 后端直接返回上周那个批次、
 * 本次一个服务都没发，而页面显示「发布已启动」——这种「假成功」比重复发布更难发现。
 *
 * 实现来源：`src/pages/technical/memberTrainingWorkspace/FactSyncRetryButton.jsx:6`
 * 🔴 不要用 `crypto.randomUUID()`：它要求安全上下文，本地用 IP 访问
 *    （`http://192.168.x.x:1300`）会直接抛 `not available on non-secure origin`；
 *    `crypto.getRandomValues` 不受此限，所以手搓 UUID v4，也不引第三方 uuid 包。
 */
import { useCallback, useRef } from 'react';

/** UUID v4（16 字节随机数 + version/variant 位修正） */
export function newRequestId() {
  if (
    typeof window === 'undefined' ||
    !window.crypto ||
    !window.crypto.getRandomValues
  ) {
    throw new Error('当前环境不支持安全随机数，无法生成幂等 ID');
  }
  const bytes = new Uint8Array(16);
  window.crypto.getRandomValues(bytes);
  bytes[6] = (bytes[6] & 15) | 64; // version 4
  bytes[8] = (bytes[8] & 63) | 128; // variant 10
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join(
    '',
  );
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(
    12,
    16,
  )}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

/**
 * 弹窗会话（`/batch/create`）。
 *
 * | 时机 | 动作 |
 * |---|---|
 * | 弹窗 visible: false → true | `open()` 生成 |
 * | 弹窗内改勾选/换分支 | 不换（本 hook 不提供任何换 ID 入口） |
 * | 提交失败（含 07020041 重弹确认、07020056 处理中） | **不调 open()**，用 `current()` 原 ID 重发 |
 * | 提交成功关闭 | `close()` 丢弃 |
 * | 页面刷新/路由重进 | 组件重建 → 新会话 → 新 ID |
 */
export default function useRequestSession() {
  // 用 ref 不用 state：不触发重渲染、不进依赖数组、也不会被序列化到任何存储里
  const ref = useRef(null);

  const open = useCallback(() => {
    ref.current = newRequestId();
    return ref.current;
  }, []);

  /** 提交时取。返回 null = 会话没开（调用方必须拦，不许裸发一个无 ID 的请求） */
  const current = useCallback(() => ref.current, []);

  const close = useCallback(() => {
    ref.current = null;
  }, []);

  return { open, current, close };
}

/**
 * 四个写操作（`batch/stop`、`batch/republish`、`service/stop`、`service/retry`）的 ID。
 *
 * 🔴 口径对齐（两份文档写法不一致，这里取「两边都不违背」的解，见进度存档 §4）：
 *   - 对接文档 §6：一次点击会话一把，失败重试沿用同一把；
 *   - 方案 §5.1：每次点击生成一次性 ID，成功/失败都不复用
 *     （复用会让第二次「重发服务 A」被后端当成第一次的回放 → 用户以为发了、其实一个都没发）。
 *   解法：**请求在飞期间**复用同一把（连点被幂等吞掉，正是本意），
 *   请求一旦返回（成功或失败）就释放，用户下一次主动点击必然拿到新 ID。
 */
export function useActionRequest() {
  const ref = useRef(null);
  const inflight = useRef(false);

  /** 点击开始：在飞则复用，否则新起一把 */
  const begin = useCallback(() => {
    if (inflight.current && ref.current) return ref.current;
    ref.current = newRequestId();
    inflight.current = true;
    return ref.current;
  }, []);

  /** 请求返回（成功或失败都要调，放 finally 里）：释放，下次点击换新 ID */
  const settle = useCallback(() => {
    inflight.current = false;
    ref.current = null;
  }, []);

  return { begin, settle, current: () => ref.current };
}
